// @flow
import React, {useEffect, useMemo, useState} from 'react';
import {Switch, Route} from 'react-router-dom';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, FormControlLabel, Divider} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
// import pedidosIcon from '../../../../assets/img/pedidosIcon.svg';
import clientsIcon from '../../../../assets/img/clientsIcon.svg';
import {usePage, usePageTab} from '../../../page/utils';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import {Controller, useForm} from 'react-hook-form';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import type {
  Store,
  SalesProcessInformation,
  OrderState,
  OrderStateMessageConfig,
  Contact,
  User,
} from '../../../../entities';

import PremiumFeatureBadge from '../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import NewFeatureBadge from '../../../../elements/NewFeatureBadge/NewFeatureBadge';
import {
  ORDER_STATE_STRINGS,
  ORDER_STATE_COLORS,
  ORDER_STATE_CHANGE_DESCRIPTIONS,
  ORDER_STATE_CHANGE_MESSAGE_PLACEHOLDERS,
} from '../../../../entities/order';
import {generateWhatsappLink, replaceTemplate} from '../../../../utils/share';
import CustomTextEditor from '../../../../elements/CustomTextEditor/CustomTextEditor';
import {useIsFeatureInTier} from '../../../../utils/component';
import CheckoutConfigContainer from '../containers/CheckoutConfigContainer';
import NewCheckoutInputContainer from '../containers/NewCheckoutInputContainer';

import {useTypedSelector} from '../../../../store';
import {debounce} from '../../../../utils/functions';

import type {TextState, TextAreaTextApi} from '@uiw/react-md-editor';
import PageTab from '../../../../elements/PageTab/PageTab';
import PageTabWrapper from '../../../../elements/PageTab/PageTabWrapper';
import {useIsMinimalPlan} from '../../../store/utils';
import NotificationDisabledModal from './Modals/NotificationDisabledModal';
import SaleRecipes from './SaleRecipes/SaleRecipes';
import CustomChip from '../../../../elements/CustomChip';
import {useTaxInvoiceEnabled} from '../../../tax-invoice/utils';
import StoreComplaints from './StoreComplaints';
import StockSettings from './StockSettings';

type Props = {|
  store: Store,
  isFetchingSalesProcessInformation?: boolean,
  isFetchingOrderStateMessages?: boolean,
  isSaving?: boolean,
  error?: string,
  getSalesProcessInformation: () => void,
  editCheckoutInput: ({checkoutInputRequest: {[key: string]: any}}) => void,
  createCheckoutInput: ({checkoutInputRequest: {[key: string]: any}}) => void,
  salesProcessInformation?: SalesProcessInformation,
  getOrderStateMessages: () => void,
  updateOrderStateMessages: (request: {[key: string]: any}) => void,
  orderStateMessages?: OrderStateMessageConfig[],
  updateSuccess?: boolean,
  resetState: () => void,
  contact?: Contact[],
  user?: User,
|};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    // display: 'flex',
    // flexDirection: 'column',
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  description: {
    fontSize: '0.875rem',
    color: colors.grey800,
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
    width: '100%',
    borderColor: colors.grey200,
  },
  settingRow: {
    // marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.grey900,
  },
  switchContainer: {
    margin: theme.spacing(0, 1),
  },
  multiline: {
    marginTop: theme.spacing(2),
  },
  label: {
    fontSize: '14px',
    color: colors.grey800,
    fontWeight: 'normal',
    lineHeight: '22px',
  },
  settingsHeader: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: 12,
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    // textAlign: 'left',
    // justifyContent: 'flex-start',
    backgroundColor: colors.grey300,
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  subRouteNameText: {
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  subRouteIcon: {
    fontSize: '1.5rem !important',
    marginRight: 12,
  },
  settingSectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  settingSectionTitle: {
    // marginLeft: theme.spacing(2),
    fontWeight: 600,
    color: colors.grey900,
  },
  settingName: {
    fontSize: '0.875rem',
    color: colors.grey900,
  },
  orderStateMessageConfigSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  stateInfoCard: {
    position: 'relative',
    width: '100%',
    height: 'auto',
    padding: theme.spacing(2, 3),
    marginTop: theme.spacing(2),
    backgroundColor: colors.grey100,
    border: `1px solid ${colors.grey300}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  leftBorder: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 5,
    height: '100%',
  },
  stateInfoContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
  },
  stateInfoTitle: {
    paddingRight: 144,
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.grey900,
  },
  stateInfoDescription: {
    paddingRight: 144,
    fontSize: '0.875rem',
    color: colors.grey900,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
      paddingRight: 0,
    },
  },
  stateMessageSwitch: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  actionButton: {
    padding: '5px 15px',
    fontSize: '12px',
    color: '#7B39FF',
    border: `1px solid ${colors.purple3}`,
    fontFamily: 'Poppins',
    fontWeight: 600,
    borderRadius: '20px',
  },
  buttonsRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'right',
  },
  stateMessageSaveButton: {
    marginLeft: theme.spacing(2),
  },
  salesTabs: {
    width: '100%',
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
  },
  pageTabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
}));

export default usePage<Props>(SalesProcess, 'Sales Process Configuration');

const initialMessages = (() => {
  const result = {};
  for (const key of Object.keys(ORDER_STATE_STRINGS)) {
    result[key] = '';
  }
  return result;
})();

type PageTabItem = {
  name: string,
  index: number,
  disabled?: boolean,
  badgeLabel?: string,
  show?: boolean,
};

function SalesProcess(props: Props) {
  const classes = useStyles();
  const {
    isFetchingSalesProcessInformation,
    isFetchingOrderStateMessages,
    isSaving,
    getSalesProcessInformation,
    editCheckoutInput,
    createCheckoutInput,
    salesProcessInformation,
    getOrderStateMessages,
    orderStateMessages,
    updateOrderStateMessages,
    error,
    updateSuccess,
    resetState,
    contact,
    user,
  } = props;

  const isTierDisabled = useIsMinimalPlan();

  const {selectedTab, onSelectTab} = usePageTab();

  const hasBasicCustomCheckoutFeature = useIsFeatureInTier(
    'BASIC_CUSTOM_ORDER_CHECKOUT'
  );

  const updateInputSuccess = useTypedSelector(
    state => state.salesProcessConfig.saveCheckoutInputSuccess
  );

  const saveInputError = useTypedSelector(
    state => state.salesProcessConfig.saveInputError
  );

  const hasOrderStateChangeMessageFeature = useIsFeatureInTier(
    'ORDER_STATE_CHANGE_MESSAGES'
  );
  const hasCustomerComplaintFeature = useIsFeatureInTier('CUSTOMER_COMPLAINT');

  const hasTaxInvoiceSettings = useIsFeatureInTier('STORE_TAX_INVOICE_SETTING');

  const taxInvoiceEnabled = useTaxInvoiceEnabled();

  const hasOrderRequiredInformationFeature = useIsFeatureInTier(
    'ORDER_REQUIRED_INFORMATION'
  );

  const [openNotifModal, setOpenNotifModal] = useState<boolean>(true);

  useEffect(() => {
    getSalesProcessInformation();
    if (hasOrderStateChangeMessageFeature) getOrderStateMessages();
  }, [
    getOrderStateMessages,
    getSalesProcessInformation,
    hasOrderStateChangeMessageFeature,
  ]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const {register, watch, setValue, control} = useForm({
    mode: 'onChange',
  });

  const watchAllValues = watch();

  // Define all tabs with consistent indices
  const ALL_TABS: PageTabItem[] = [
    {
      name: 'Campos adicionales',
      index: 0,
      show: true,
    },
    {
      name: 'Notificaciones',
      index: 1,
      show: true,
    },
    {
      name: 'Comprobantes de venta',
      index: 2,
      disabled: !hasTaxInvoiceSettings,
      badgeLabel: hasTaxInvoiceSettings ? undefined : 'Mejorar Plan',
      show: taxInvoiceEnabled,
    },
    {
      name: 'Configuración de stock',
      index: 3,
      show: true,
    },
    {
      name: 'Quejas y reclamos',
      index: 4,
      show: true,
      badgeLabel: hasCustomerComplaintFeature ? undefined : 'Mejorar Plan',
    },
  ];

  // Filter tabs based on show condition
  const SALES_TABS = ALL_TABS.filter(tab => tab.show);

  const inputNameWrap = {
    enabledIdentificationNumber: 'Documento de identidad',
    enabledDeliveryDate: 'Fecha de entrega',
    enabledComment: 'Comentario',
  };

  const getDefaultInput = (name: string) => {
    const realInputName = inputNameWrap[name] ?? '';

    return salesProcessInformation?.find(el => el.name === realInputName);
  };

  const toggleDefaultInput = (name: string, e: any) => {
    const realInputName = inputNameWrap[name] ?? '';
    const defaultInput = getDefaultInput(name);

    if (defaultInput) {
      editCheckoutInput({
        checkoutInputRequest: {
          id: defaultInput.id,
          enabled: e.target.checked,
        },
      });
    } else {
      if (realInputName) {
        createCheckoutInput({
          checkoutInputRequest: {
            name: realInputName,
            enabled: e.target.checked,
            required: false,
            inputType: 'TEXT',
          },
        });
      }
    }
  };

  const onDescriptionChange = debounce(
    (name: string, value: string) => {
      const defaultInput = getDefaultInput(name);

      if (defaultInput) {
        editCheckoutInput({
          checkoutInputRequest: {
            id: defaultInput.id,
            description: value,
          },
        });
      }
    },
    [500]
  );

  const [messages, setMessages] = useState(initialMessages);
  // const [messagesEnabled, setMessagesEnabled] = useState({});

  useEffect(() => {
    if (orderStateMessages) {
      const result: {[key: string]: any} = {...initialMessages};
      // const switches = {};
      for (const messageConfig of orderStateMessages) {
        result[messageConfig.orderState] = messageConfig.message;
        // switches[messageConfig.orderState] = messageConfig.enabled;
      }
      setMessages(result);
      // setMessagesEnabled(switches);
    }
  }, [orderStateMessages]);

  const whatsappContact: ?Contact = useMemo(() => {
    if (contact && contact.length)
      return contact.find(c => c.contactType.name === 'WHATS_APP_NUMBER');
  }, [contact]);

  const handleOpenPreviewLink = (state: OrderState) => {
    if (state && messages && messages[state]) {
      const dataToReplace = {
        nombre_cliente: user ? user.firstName : undefined,
        link_de_pago: `https://sweetngo.tiendada.com/order/OR-46A52B24?token=3VFbiFCgm3MMZSpNK3XzrA (link de ejemplo)`,
        monto_de_orden: `S/ 50.00`,
        link_de_review: `https://sweetngo.tiendada.com/order/OR-46A52B24/review?token=3VFbiFCgm3MMZSpNK3XzrA (link de ejemplo)`,
      };
      const replacedText = replaceTemplate(messages[state], dataToReplace);
      const whatsappLink = generateWhatsappLink(
        (whatsappContact && whatsappContact.data) || '',
        replacedText
      );
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleStateChangeMessageSwitch = (event, orderState: OrderState) => {
    const request = {
      enabled: event.target.checked,
      orderState,
    };
    updateOrderStateMessages({request});
  };

  const handleSubmitOrderStateMessage = (orderState: OrderState) => {
    const currentState =
      !!orderStateMessages &&
      orderStateMessages.find(config => config.orderState === orderState);
    if (!currentState) {
      setValue(`${orderState}-message-enabled`, true);
    }
    const request = {
      enabled: !currentState ? true : undefined,
      message: messages[orderState] || undefined,
      orderState,
    };
    updateOrderStateMessages({request});
  };

  return (
    <>
      <Switch>
        <Route exact path="/configure/sales-process">
          {isTierDisabled && (
            <NotificationDisabledModal
              open={openNotifModal}
              handleClose={() => setOpenNotifModal(false)}
            />
          )}
          {/* {selectedTab === 1 && <NotificationDisabledModal />} */}
          <SnackBar
            mode="success"
            className={classes.snackBarSuccess}
            message={
              updateSuccess || updateInputSuccess
                ? 'Se guardaron los cambios exitosamente'
                : undefined
            }
          />
          <SnackBar
            mode="error"
            className={classes.snackBarError}
            message={error}
          />
          {saveInputError && (
            <SnackBar
              mode="error"
              className={classes.snackBarError}
              message="No se pudo actualizar."
            />
          )}
          <div className={classes.salesTabs}>
            {SALES_TABS.map(({name, index, disabled, badgeLabel}) => (
              <PageTab
                key={name}
                tab={index}
                selectedTab={selectedTab}
                onSelectTab={v => {
                  onSelectTab(v);
                  if (v === 1) setOpenNotifModal(true);
                }}
                disabled={disabled}
              >
                <div className={classes.pageTabLabel}>
                  <span>{name}</span>
                  {badgeLabel && (
                    <CustomChip
                      label={badgeLabel}
                      color="primary"
                      size="small"
                    />
                  )}
                </div>
              </PageTab>
            ))}
          </div>
          <PageTabWrapper tab={0} tabSelected={selectedTab}>
            {!isFetchingSalesProcessInformation && (
              <div className={classes.root}>
                <Typography
                  className={classes.settingTitle}
                  style={{fontSize: '18px'}}
                >
                  Data de cliente
                </Typography>
                <Typography className={classes.description}>
                  Selecciona los campos adicionales que quieres agregar a tu
                  proceso de venta
                </Typography>
                <Divider className={classes.divider} />
                <div className={classes.settingRow}>
                  <Typography className={classes.settingTitle}>
                    Documento de identidad
                  </Typography>
                  <FormControlLabel
                    // disabled={isSaving}
                    control={
                      <IosSwitch
                        onChange={e =>
                          toggleDefaultInput('enabledIdentificationNumber', e)
                        }
                        defaultChecked={
                          getDefaultInput('enabledIdentificationNumber')
                            ?.enabled
                        }
                        className={classes.switchContainer}
                        name="enabledIdentificationNumber"
                        disabled={!hasOrderRequiredInformationFeature}
                        inputRef={register()}
                      />
                    }
                    labelPlacement="start"
                    label={
                      <div className={classes.label}>
                        {watchAllValues.enabledIdentificationNumber ||
                        getDefaultInput('enabledIdentificationNumber')?.enabled
                          ? 'Activado'
                          : 'Desactivado'}
                      </div>
                    }
                  />
                </div>
                {hasBasicCustomCheckoutFeature && (
                  <>
                    <Divider className={classes.divider} />
                    <div className={classes.settingRow}>
                      <Typography className={classes.settingTitle}>
                        Fecha de entrega
                      </Typography>
                      <FormControlLabel
                        control={
                          <IosSwitch
                            onChange={e =>
                              toggleDefaultInput('enabledDeliveryDate', e)
                            }
                            defaultChecked={
                              getDefaultInput('enabledDeliveryDate')?.enabled
                            }
                            className={classes.switchContainer}
                            name="enabledDeliveryDate"
                            disabled={!hasOrderRequiredInformationFeature}
                            inputRef={register()}
                          />
                        }
                        labelPlacement="start"
                        label={
                          <div className={classes.label}>
                            {watchAllValues.enabledDeliveryDate ||
                            getDefaultInput('enabledDeliveryDate')?.enabled
                              ? 'Activado'
                              : 'Desactivado'}
                          </div>
                        }
                      />
                    </div>
                    {(typeof watchAllValues.enabledDeliveryDate === 'undefined'
                      ? !!getDefaultInput('enabledDeliveryDate')?.enabled
                      : watchAllValues.enabledDeliveryDate) && (
                      <div>
                        <CustomTextField
                          multiline
                          onChange={e =>
                            onDescriptionChange(
                              'enabledDeliveryDate',
                              e.target.value
                            )
                          }
                          className={classes.multiline}
                          label="Especificaciones (Opcional)"
                          name="enabledDeliveryDateDescription"
                          type="text"
                          inputRef={register()}
                          disabled={!hasOrderRequiredInformationFeature}
                          defaultValue={
                            getDefaultInput('enabledDeliveryDate')?.description
                          }
                          rows={2}
                        />
                      </div>
                    )}
                    <Divider className={classes.divider} />
                    <div className={classes.settingRow}>
                      <Typography className={classes.settingTitle}>
                        Comentario
                      </Typography>
                      <FormControlLabel
                        // disabled={isSaving}
                        control={
                          <IosSwitch
                            onChange={e =>
                              toggleDefaultInput('enabledComment', e)
                            }
                            defaultChecked={
                              getDefaultInput('enabledComment')?.enabled
                            }
                            className={classes.switchContainer}
                            name="enabledComment"
                            inputRef={register()}
                            disabled={!hasOrderRequiredInformationFeature}
                          />
                        }
                        labelPlacement="start"
                        label={
                          <div className={classes.label}>
                            {watchAllValues.enabledComment ||
                            getDefaultInput('enabledComment')?.enabled
                              ? 'Activado'
                              : 'Desactivado'}
                          </div>
                        }
                      />
                    </div>
                    {(typeof watchAllValues.enabledComment === 'undefined'
                      ? !!getDefaultInput('enabledComment')?.enabled
                      : watchAllValues.enabledComment) && (
                      <div>
                        <CustomTextField
                          className={classes.multiline}
                          multiline
                          onChange={e =>
                            onDescriptionChange(
                              'enabledComment',
                              e.target.value
                            )
                          }
                          label="Especificaciones (Opcional)"
                          name="enabledCommentDescription"
                          type="text"
                          rows={2}
                          defaultValue={
                            getDefaultInput('enabledComment')?.description
                          }
                          disabled={!hasOrderStateChangeMessageFeature}
                          inputRef={register()}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <CheckoutConfigContainer />
          </PageTabWrapper>

          <PageTabWrapper tab={1} tabSelected={selectedTab}>
            {!isFetchingOrderStateMessages && (
              <div className={classes.root}>
                <div className={classes.settingSectionTitleRow}>
                  <div
                    className={classes.settingTitle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img src={clientsIcon} alt="icon" />
                    <Typography className={classes.settingSectionTitle}>
                      Gestión de clientes
                    </Typography>
                  </div>
                  {!hasOrderStateChangeMessageFeature ? (
                    <PremiumFeatureBadge />
                  ) : (
                    <NewFeatureBadge />
                  )}
                </div>
                <Typography className={classes.description}>
                  Mensajes para tu cliente que son activados a partir del cambio
                  de estado de la orden. Te permitirá mantener a tu cliente
                  informado sobre su compra en cada parte del procesos y ahorrar
                  tiempo de gestión.
                </Typography>
                <Divider className={classes.divider} />
                {Object.keys(ORDER_STATE_STRINGS).map((state, i) => {
                  const defaultChecked =
                    !!orderStateMessages &&
                    orderStateMessages.findIndex(
                      config => config.orderState === state && config.enabled
                    ) >= 0;
                  if (i < 1) return null;
                  return (
                    <div
                      key={`${state}-message-config`}
                      className={classes.orderStateMessageConfigSection}
                    >
                      <div className={classes.stateInfoCard}>
                        <div
                          className={classes.leftBorder}
                          style={{
                            backgroundColor: ORDER_STATE_COLORS[state].main,
                          }}
                        />
                        <div className={classes.stateInfoContent}>
                          <Typography className={classes.stateInfoTitle}>
                            {`Estado de la orden: ${ORDER_STATE_STRINGS[state]}`}
                          </Typography>
                          <Typography className={classes.stateInfoDescription}>
                            {ORDER_STATE_CHANGE_DESCRIPTIONS[state]}
                          </Typography>
                          <FormControlLabel
                            className={classes.stateMessageSwitch}
                            control={
                              <Controller
                                name={`${state}-message-enabled`}
                                defaultValue={defaultChecked}
                                control={control}
                                render={({
                                  onChange,
                                  onBlur,
                                  value,
                                  defaultValue,
                                  name,
                                  ref,
                                }) => (
                                  <IosSwitch
                                    checked={value}
                                    defaultChecked={defaultValue}
                                    className={classes.switchContainer}
                                    inputRef={ref}
                                    onChange={e => {
                                      onChange(e.target.checked);
                                      handleStateChangeMessageSwitch(e, state);
                                    }}
                                    disabled={
                                      !hasOrderStateChangeMessageFeature ||
                                      isSaving ||
                                      !hasOrderStateChangeMessageFeature
                                    }
                                  />
                                )}
                              />
                            }
                            labelPlacement="start"
                            label={
                              <div className={classes.label}>
                                {watch(`${state}-message-enabled`)
                                  ? 'Activado'
                                  : 'Desactivado'}
                              </div>
                            }
                          />
                        </div>
                      </div>
                      <Typography
                        className={classes.settingTitle}
                        style={{marginTop: 16, marginBottom: 8}}
                      >
                        Mensaje:
                      </Typography>
                      <CustomTextEditor
                        text={messages[state] || ''}
                        onChange={s =>
                          setMessages(prev => {
                            const aux = {...prev};
                            aux[state] = s;
                            return aux;
                          })
                        }
                        placeholder={
                          ORDER_STATE_CHANGE_MESSAGE_PLACEHOLDERS[state]
                        }
                        extraCommands={[
                          {
                            name: 'Customer name',
                            enabled: true,
                            keyCommand: 'nombre_cliente',
                            buttonProps: {'aria-label': 'Insert customer name'},
                            icon: (
                              <div className={classes.actionButton}>
                                Nombre de cliente
                              </div>
                            ),
                            execute: (
                              state: TextState,
                              api: TextAreaTextApi
                            ) => {
                              api.replaceSelection('{{nombre_cliente}}');
                            },
                          },
                          {
                            name: 'Payment link',
                            enabled: true,
                            keyCommand: 'link_de_pago',
                            buttonProps: {'aria-label': 'Insert payment link'},
                            icon: (
                              <div className={classes.actionButton}>
                                Link de pago
                              </div>
                            ),
                            execute: (
                              state: TextState,
                              api: TextAreaTextApi
                            ) => {
                              api.replaceSelection('{{link_de_pago}}');
                            },
                          },
                          {
                            name: 'Order amount',
                            enabled: true,
                            keyCommand: 'monto_de_orden',
                            buttonProps: {'aria-label': 'Insert order amount'},
                            icon: (
                              <div className={classes.actionButton}>
                                Monto de la orden
                              </div>
                            ),
                            execute: (
                              state: TextState,
                              api: TextAreaTextApi
                            ) => {
                              api.replaceSelection('{{monto_de_orden}}');
                            },
                          },
                          state === 'DELIVERED'
                            ? {
                                name: 'Review link',
                                enabled: true,
                                keyCommand: 'link_de_review',
                                buttonProps: {
                                  'aria-label': 'Insert review link',
                                },
                                icon: (
                                  <div className={classes.actionButton}>
                                    Link de reseña
                                  </div>
                                ),
                                execute: (
                                  state: TextState,
                                  api: TextAreaTextApi
                                ) => {
                                  api.replaceSelection('{{link_de_review}}');
                                },
                              }
                            : undefined,
                        ]}
                        disabled={!hasOrderStateChangeMessageFeature}
                      />
                      <div className={classes.buttonsRow}>
                        <CustomButton
                          variant="outlined"
                          onClick={() => handleOpenPreviewLink(state)}
                          disabled={
                            !hasOrderStateChangeMessageFeature ||
                            !messages[state]
                          }
                        >
                          Vista Previa
                        </CustomButton>
                        <CustomButton
                          variant="contained"
                          className={classes.stateMessageSaveButton}
                          onClick={() => handleSubmitOrderStateMessage(state)}
                          disabled={
                            !hasOrderStateChangeMessageFeature || isSaving
                          }
                        >
                          Guardar Cambios
                        </CustomButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PageTabWrapper>

          <PageTabWrapper tab={2} tabSelected={selectedTab}>
            {taxInvoiceEnabled && (
              <div className={classes.root}>
                <div className={classes.settingSectionTitleRow}>
                  <div
                    className={classes.settingTitle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography className={classes.settingSectionTitle}>
                      Configura los permisos de tus comprobantes de pago
                    </Typography>
                  </div>
                </div>

                <SaleRecipes />
              </div>
            )}
          </PageTabWrapper>

          <PageTabWrapper tab={3} tabSelected={selectedTab}>
            <StockSettings />
          </PageTabWrapper>

          <PageTabWrapper tab={4} tabSelected={selectedTab}>
            <StoreComplaints />
          </PageTabWrapper>
        </Route>

        <Route
          exact
          path="/configure/sales-process/checkout/:id"
          component={NewCheckoutInputContainer}
        />
        {/* <Route
          exact
          path="/configure/sales-process/checkout"
          component={CheckoutConfigContainer}
        /> */}
      </Switch>
    </>
  );
}

SalesProcess.propTypes = {
  store: PropTypes.object.isRequired,
  isFetchingSalesProcessInformation: PropTypes.bool,
  isFetchinOrderStateMessages: PropTypes.bool,
  updateSuccess: PropTypes.bool,
  error: PropTypes.string,
  getSalesProcessInformation: PropTypes.func.isRequired,
  editCheckoutInput: PropTypes.func.isRequired,
  createCheckoutInput: PropTypes.func.isRequired,
  salesProcessInformation: PropTypes.object,
  getOrderStateMessages: PropTypes.func.isRequired,
  updateOrderStateMessages: PropTypes.func.isRequired,
  orderStateMessages: PropTypes.array,
  resetState: PropTypes.func,
  contact: PropTypes.array,
  user: PropTypes.object,
};
